import clsx from "clsx";
import { useState } from "react";
// eslint-disable-next-line no-restricted-imports
import { toast } from "react-toastify";

import { serverRefreshGitbook } from "api/api-server-ai";
import { AIModelType } from "components/server-types";
import { t } from "i18n/i18n";
import { LegacyButton } from "mds/components/LegacyButton";
import { Menu, MenuItem } from "mds/components/Menu";
import { ChevronDownIcon, GearSettingsIcon, RefreshIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store";
import { selectCurrentUserIsMPAdmin, selectUserSelectedAIModel } from "store/selectors";
import { aiStateActions } from "store/slices/ai";
import { toastErrorMessage, toastSuccessMessage } from "utils/alerts";

// Default model for the chat panel, should be fast and mainly rely on good context to give good responses.

export const DEFAULT_MODEL_FOR_CHAT: AIModelType = AIModelType.GPT_4_1_NANO;

// For assessment, ensure we use a multimodal model that can read files (images/PDFs,etc.)

export const DEFAULT_MODEL_FOR_ASSESSMENT_QUERY: AIModelType = AIModelType.GPT_5_MINI;

export const AISettings = () => {
  const dispatch = useAppDispatch();
  const isMPAdmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const userSelectedAIModel = useAppSelector(selectUserSelectedAIModel);

  const handleRefreshGitbook = async () => {
    setIsRefreshing(true);
    const toastId = toast.loading("Refreshing Gitbook pages...");

    try {
      await serverRefreshGitbook();
      toast.dismiss(toastId);
      toastSuccessMessage("Gitbook pages refreshed successfully");
    } catch (error) {
      console.error(error);
      toast.dismiss(toastId);
      toastErrorMessage("Error refreshing Gitbook pages");
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Menu
      direction="bottom"
      menuButton={
        <LegacyButton kind="secondary" size="s">
          <GearSettingsIcon />
        </LegacyButton>
      }
    >
      <div className="body-s flex w-[400px] max-w-full flex-col items-start gap-2 py-2">
        <div className="h4">{t("ai.settings.title")}</div>
        {isMPAdmin && (
          <LegacyButton
            className=""
            disabled={isRefreshing}
            kind="secondary"
            size="xs"
            onClick={handleRefreshGitbook}
          >
            <RefreshIcon className="mr-1" />
            Refresh Gitbook
          </LegacyButton>
        )}

        <Menu
          direction="bottom"
          menuButton={
            <LegacyButton className="mb-2" kind="secondary" size="xs">
              <div className="mr-1">
                Model: {userSelectedAIModel ? userSelectedAIModel.toUpperCase() : "Default"}
              </div>
              <ChevronDownIcon />
            </LegacyButton>
          }
        >
          {[null, ...Object.values(AIModelType)].map((modelOption) => {
            const selected = userSelectedAIModel === modelOption;
            return (
              <MenuItem
                checked={selected}
                className={clsx({ selected, "text-white": selected })}
                key={modelOption}
                value={modelOption}
                onClick={(e) =>
                  dispatch(aiStateActions.setUserSelectedAIModel(e.value as AIModelType))
                }
              >
                {/* Users can select "Default" to clear their local storage and revert to using the default */}
                {/* model (DEFAULT_MODEL_FOR_CHAT or DEFAULT_MODEL_FOR_ASSESSMENT_QUERY) again. */}
                {modelOption ? modelOption.toUpperCase() : "Default"}
              </MenuItem>
            );
          })}
        </Menu>
      </div>
    </Menu>
  );
};
